import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "@/i18n/translations";
import { RefreshCw, Unplug, CalendarCheck, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";

const ERR = {
  invalid_company_id: "Укажите числовой ID филиала (например 773297 или n773297)",
  invalid_partner_key: "Партнёрский ключ не принят Altegio. Проверьте ключ в Developer Hub",
  company_not_found: "Филиал с таким ID не найден в Altegio",
  invalid_user_token: "Пользовательский токен не принят Altegio (нет доступа к журналу записи)",
  altegio_unreachable: "Altegio недоступен, попробуйте позже",
  not_connected: "Altegio не подключён",
};

// Integrations → Altegio: per-salon partner key + branch id (+ optional user token for journal/clients)
export default function AltegioConnect({ API }) {
  const { t } = useTranslation();
  const tf = (k, fb) => { const v = t(k); return v && v !== k ? v : fb; };
  const [st, setSt] = useState(null);
  const [form, setForm] = useState({ company_id: "", partner_token: "", user_token: "" });
  const [newUserToken, setNewUserToken] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/altegio/status`).then((r) => setSt(r.data)).catch(() => setSt({ connected: false }));
  useEffect(() => { load(); }, []);

  const errText = (e) => { const d = e?.response?.data?.detail || ""; return ERR[d] || d || tf("altegio.err.generic", "Ошибка подключения"); };
  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); setSt(r.data); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  const connect = () => run(() => axios.post(`${API}/altegio/connect`, {
    company_id: form.company_id.trim(), partner_token: form.partner_token.trim(), user_token: form.user_token.trim() || null,
  }), tf("altegio.connected", "Altegio подключён, данные загружены"));
  const sync = () => run(() => axios.post(`${API}/altegio/sync`), tf("altegio.synced", "Данные Altegio обновлены"));
  const patch = (body, msg) => run(() => axios.patch(`${API}/altegio/settings`, body), msg);
  const disconnect = async () => {
    if (!window.confirm(tf("altegio.disconnectConfirm", "Отключить Altegio? Будущие записи, импортированные из Altegio, будут убраны из календаря."))) return;
    setBusy(true);
    try { await axios.delete(`${API}/altegio/disconnect`); setSt({ connected: false }); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  if (!st) return null;
  const c = st.counts || {};

  return (
    <div data-testid="altegio-block" className="rounded-xl border-2 border-black p-4 sm:p-6 bg-[#F0FDF4]">
      <div className="flex items-center gap-2 mb-1">
        <CalendarCheck className="w-5 h-5" />
        <span className="font-semibold text-base md:text-lg">Altegio</span>
        {st.connected && <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-green-700" data-testid="altegio-status-badge"><CheckCircle2 className="w-3.5 h-3.5" /> {tf("altegio.active", "ПОДКЛЮЧЕНО")}</span>}
      </div>
      <p className="text-sm text-gray-600 mb-4">
        {tf("altegio.desc", "Онлайн-журнал Altegio (ex-YClients): ИИ записывает клиентов прямо в Altegio, услуги и мастера подтягиваются в прайс, записи показываются в календаре Chatity, база клиентов импортируется в CRM.")}
      </p>

      {!st.connected ? (
        <div className="space-y-3" data-testid="altegio-connect-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{tf("altegio.companyId", "ID филиала")}</Label>
              <Input data-testid="altegio-company-id" placeholder="773297" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} />
              <p className="text-[11px] text-gray-500 mt-1">{tf("altegio.companyIdHint", "Виден в адресе app.alteg.io/timetable/XXXXX или в ссылке онлайн-записи nXXXXX.alteg.io")}</p>
            </div>
            <div>
              <Label className="text-xs">{tf("altegio.partnerKey", "Партнёрский ключ (Access key)")}</Label>
              <Input data-testid="altegio-partner-token" placeholder="sgz4…" value={form.partner_token} onChange={(e) => setForm({ ...form, partner_token: e.target.value })} />
              <p className="text-[11px] text-gray-500 mt-1">{tf("altegio.partnerKeyHint", "Altegio → Настройки → Интеграции и доступ → Ключи → Developer Hub")}</p>
            </div>
          </div>
          <div>
            <Label className="text-xs">{tf("altegio.userToken", "Пользовательский токен (необязательно)")}</Label>
            <Input data-testid="altegio-user-token" placeholder="User token" value={form.user_token} onChange={(e) => setForm({ ...form, user_token: e.target.value })} />
            <p className="text-[11px] text-gray-500 mt-1">{tf("altegio.userTokenHint", "Нужен, чтобы читать журнал записей и базу клиентов, а также переносить/отменять записи. Без него ИИ сможет только создавать записи через онлайн-запись.")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button data-testid="altegio-connect-btn" onClick={connect} disabled={busy || !form.company_id.trim() || !form.partner_token.trim()} className="bg-black text-white hover:bg-gray-800">
              {busy ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}{tf("altegio.connect", "Подключить")}
            </Button>
            <a href="https://developer.alteg.io/en/get-api-key" target="_blank" rel="noreferrer" className="text-xs underline inline-flex items-center gap-1 text-gray-600">
              {tf("altegio.howToKeys", "Как получить ключи")} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="altegio-connected">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <div className="rounded-lg bg-white border p-2"><div className="text-[11px] text-gray-500">{tf("altegio.branch", "Филиал")}</div><div className="font-semibold truncate" data-testid="altegio-company-title">{st.company_title || st.company_id}</div><div className="text-[11px] text-gray-400">ID {st.company_id} · {st.company_tz}</div></div>
            <div className="rounded-lg bg-white border p-2"><div className="text-[11px] text-gray-500">{tf("altegio.staffServices", "Мастера / услуги")}</div><div className="font-semibold" data-testid="altegio-count-team">{c.staff ?? 0} / {c.services ?? 0}</div></div>
            <div className="rounded-lg bg-white border p-2"><div className="text-[11px] text-gray-500">{tf("altegio.bookings", "Записи в календаре")}</div><div className="font-semibold" data-testid="altegio-count-bookings">{c.bookings ?? 0}</div></div>
            <div className="rounded-lg bg-white border p-2"><div className="text-[11px] text-gray-500">{tf("altegio.clients", "Клиентов в CRM")}</div><div className="font-semibold" data-testid="altegio-count-clients">{c.clients ?? 0}</div></div>
          </div>
          <div className="text-xs text-gray-500">
            {tf("altegio.key", "Ключ")}: {st.partner_masked} · {st.has_user_token ? tf("altegio.userTokenOk", "пользовательский токен есть") : tf("altegio.userTokenMissing", "без пользовательского токена")}
            {st.synced_at && <> · {tf("altegio.syncedAt", "обновлено")} {new Date(st.synced_at).toLocaleString()}</>}
          </div>
          {st.last_error && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 flex gap-2" data-testid="altegio-last-error"><AlertTriangle className="w-4 h-4 shrink-0" />{st.last_error}</div>}
          {!st.has_user_token && (
            <div className="rounded-lg bg-white border p-3 space-y-2" data-testid="altegio-add-user-token">
              <div className="text-xs text-gray-700">{tf("altegio.addUserToken", "Добавьте пользовательский токен, чтобы видеть журнал записей Altegio в календаре и импортировать клиентов:")}</div>
              <div className="flex gap-2">
                <Input data-testid="altegio-user-token-input" placeholder="User token" value={newUserToken} onChange={(e) => setNewUserToken(e.target.value)} />
                <Button data-testid="altegio-user-token-save" size="sm" variant="outline" disabled={busy || !newUserToken.trim()} onClick={() => patch({ user_token: newUserToken.trim() }, tf("altegio.userTokenSaved", "Токен сохранён, журнал загружен"))}>{tf("common.save", "Сохранить")}</Button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg bg-white border p-3">
            <div>
              <div className="text-sm font-medium">{tf("altegio.pushBookings", "ИИ записывает клиентов в Altegio")}</div>
              <div className="text-[11px] text-gray-500">{tf("altegio.pushBookingsHint", "Каждая подтверждённая запись из чатов и календаря Chatity создаётся в журнале Altegio")}</div>
            </div>
            <Switch data-testid="altegio-push-toggle" checked={!!st.push_bookings} disabled={busy} onCheckedChange={(v) => patch({ push_bookings: v })} />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="altegio-sync-btn" size="sm" variant="outline" onClick={sync} disabled={busy}><RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} />{tf("altegio.sync", "Обновить данные")}</Button>
            <Button data-testid="altegio-disconnect-btn" size="sm" variant="ghost" className="text-red-600" onClick={disconnect} disabled={busy}><Unplug className="w-4 h-4 mr-1" />{tf("altegio.disconnect", "Отключить")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
